import { graphql } from '@predium/client-graphql';

graphql(`
  fragment StatusQuoAnalysisPortfolio on portfolio {
    id
    name
  }
`);

graphql(`
  fragment SearchBuilding on building {
    id
    customer_external_identifier
    is_new_construction
    is_missing_energy_data
    address {
      street
      city
      postal_code
    }
    folder {
      id
    }
  }
`);

graphql(`
  fragment BuildingsBenchmarkCrrem on BuildingsBenchmarkCrremOutput {
    analysisType
    averageFinalEnergy
    averagePrimaryEnergy
    averageHeatingDemand
    averageCo2Costs
    averageEnergyCosts
    crremAnalysis {
      averageCo2Emissions
      strandingYear
      yearOfExcedance
    }
  }
`);

graphql(`
  fragment BuildingsBenchmarkNationalEnergyLaw on BuildingsBenchmarkNationalEnergyLawOutput {
    analysisType
    averageFinalEnergy
    averagePrimaryEnergy
    averageHeatingDemand
    averageCo2Costs
    averageEnergyCosts
    nationalEnergyLawAnalysis {
      averageCo2Emissions
    }
  }
`);

graphql(`
  fragment StatusQuoAnalysisBuildingBasicDetails on building {
    id
    year_constructed
    is_new_construction
    is_missing_energy_data
    description
    custom_data
    address {
      street
      postal_code
      city
      country_id
      mapbox_building_ids
      building_citydb_gml_ids
      building_lod1_ids
    }
    economic_unit {
      id
      name
      portfolio {
        ...StatusQuoAnalysisPortfolio
      }
    }
    responsible_user {
      first_name
      last_name
    }
    areas {
      id
      value
      area_type_id
      type_of_use_id
      class_of_use_id
    }
    building_valuations(order_by: { valuation_at: desc }) {
      id
      value
      valuation_at
    }
  }
`);

graphql(`
  fragment AnalyzeBuildingsCrremAnalysis on AnalyzeBuildingsCrremAnalysisOutput {
    averageCo2Emissions
    co2TargetPath
    energyTargetPath
    co2Path {
      m2
    }
    energyPath {
      m2
    }
    strandingYear
    yearOfExcedance
    cumulativeExcessEmissions
    excessEmissionCostPath
    scope123Co2Emissions {
      wholeBuilding {
        ...Scope123Co2Emissions
      }
      landlord {
        ...Scope123Co2Emissions
      }
      tenant {
        ...Scope123Co2Emissions
      }
    }
    strandingData {
      ...StrandingData
    }
  }
`);

graphql(`
  fragment AnalyzeBuildingsNationalEnergyLawAnalysis on AnalyzeBuildingsNationalEnergyLawAnalysisOutput {
    averageCo2Emissions
    co2Path {
      m2
    }
    energyPath {
      m2
    }
  }
`);

graphql(`
  fragment CustomTargets on CustomTargetResult {
    name
    final_energy {
      reached
      year
      targetValue
    }
    primary_energy {
      reached
      year
      targetValue
    }
    co2_emissions {
      reached
      year
      targetValue
    }
  }
`);

graphql(`
  fragment AnalyzeBuildingCrrem on AnalyzeBuildingCrremOutput {
    analysisType
    co2CostPath {
      total
      m2
    }
    co2CostDistributionPath {
      landlordPath {
        total
        m2
      }
      tenantPath {
        total
        m2
      }
    }
    energyCostPath {
      total
      m2
    }
    energyConsumers {
      energySourceType
      energyConsumptionM2
      energyConsumptionTotal
    }
    taxBracket
    efficiencyClass
    euTaxonomyCompliance
    finalEnergy
    primaryEnergy
    heatingDemand
    climateRisks {
      timespan
      type
      value
    }
    floorAreaByClassOfUse {
      classOfUse
      area
    }
    crremAnalysis {
      co2Emissions
      co2TargetPath
      energyTargetPath
      co2Path {
        m2
      }
      energyPath {
        m2
      }
      strandingYear
      yearOfExcedance
      cumulativeExcessEmissions
      excessEmissionCostPath
      buildingModelTransitions {
        ...BuildingModelTransition
      }
      scope123Co2Emissions {
        wholeBuilding {
          ...Scope123Co2Emissions
        }
        landlord {
          ...Scope123Co2Emissions
        }
        tenant {
          ...Scope123Co2Emissions
        }
      }
    }
    customTargets {
      ...CustomTargets
    }
    slfAnalysis {
      ...SLFAnalysis
    }
    customTargetPath {
      co2 {
        targetPath
        intersectionYear
      }
    }
  }
`);

graphql(`
  fragment AnalyzeBuildingNationalEnergyLaw on AnalyzeBuildingNationalEnergyLawOutput {
    analysisType
    co2CostPath {
      total
      m2
    }
    co2CostDistributionPath {
      landlordPath {
        total
        m2
      }
      tenantPath {
        total
        m2
      }
    }
    energyCostPath {
      total
      m2
    }
    energyConsumers {
      energySourceType
      energyConsumptionM2
      energyConsumptionTotal
    }
    taxBracket
    efficiencyClass
    euTaxonomyCompliance
    finalEnergy
    primaryEnergy
    heatingDemand
    climateRisks {
      timespan
      type
      value
    }
    floorAreaByClassOfUse {
      classOfUse
      area
    }
    nationalEnergyLawAnalysis {
      co2Emissions
      co2Path {
        m2
        total
      }
      energyPath {
        m2
        total
      }
      buildingModelTransitions {
        ...BuildingModelTransition
      }
    }
    customTargets {
      ...CustomTargets
    }
    slfAnalysis {
      ...SLFAnalysis
    }
  }
`);

graphql(`
  fragment BuildingCRREMConsumptionAnalysisOutput on GetBuildingCRREMConsumptionAnalysisOutput {
    co2TargetPath
    energyTargetPath
    co2Path {
      m2
    }
    energyPath {
      m2
    }
    strandingYear
    yearOfExcedance
    cumulativeExcessEmissions
    excessEmissionCostPath
  }
`);

graphql(`
  fragment StrandingData on StrandingDataOutput {
    year
    count
  }
`);

graphql(`
  fragment BuildingModelTransition on BuildingModelTransitionOutput {
    validFrom
    co2Intensity
    energyIntensity
  }
`);

graphql(`
  fragment Scope123Co2Emissions on Scope123Co2EmissionsOutput {
    scope1 {
      co2Emissions
      co2EmissionsTotal
    }
    scope2 {
      co2Emissions
      co2EmissionsTotal
    }
    scope3 {
      co2Emissions
      co2EmissionsTotal
    }
  }
`);

graphql(`
  fragment BuildingDetails on BuildingRowOutput {
    id
    street
    postal_code
    city
    country
    types_of_use
    first_stranding_year
    is_new_construction
    building {
      id
      description
    }
    energy_data {
      co2_emissions
      co2_emissions_total
      co2_costs
      co2_costs_total
      energy_primary
      energy_primary_total
      energy_final
      energy_final_total
      efficiency_class
      eu_taxonomy_compliance
      slf_compliant {
        quick_check
        full_check {
          report_id
          value
          issued_at
        }
      }
    }
  }
`);

graphql(`
  fragment AnalyzedBuilding on AnalyzeBuildingOutputUnion {
    ... on AnalyzeBuildingCrremOutput {
      ...AnalyzeBuildingCrrem
    }
    ... on AnalyzeBuildingNationalEnergyLawOutput {
      ...AnalyzeBuildingNationalEnergyLaw
    }
  }
`);

graphql(`
  fragment AnalyzedBuildingsBenchMark on BuildingsBenchmarkOutputUnion {
    ... on BuildingsBenchmarkCrremOutput {
      ...BuildingsBenchmarkCrrem
    }
    ... on BuildingsBenchmarkNationalEnergyLawOutput {
      ...BuildingsBenchmarkNationalEnergyLaw
    }
  }
`);

graphql(`
  fragment ConsumptionAllocationNationalEnergyLawCarbonEmission on ConsumptionAllocationNationalEnergyLawCarbonEmission {
    analysisType
    intensity
    intensityAbsolute
  }
`);

graphql(`
  fragment ConsumptionAllocationCrremCarbonEmission on ConsumptionAllocationCrremCarbonEmission {
    analysisType
    intensity
    intensityAbsolute
    scope1 {
      percentage
      value
    }
    scope2 {
      percentage
      value
    }
    scope3 {
      percentage
      value
    }
  }
`);

graphql(`
  fragment SLFAnalysis on SlfComplianceAnalysisOutput {
    lastSlfReport {
      reportId
      value
      issuedAt
    }
    ongoingReportId
    compliantFactors {
      ... on CertificateSlfComplianceAnalysisOutput {
        certificateType
        level
        score
        type
      }
      ... on EfficiencyClassSlfComplianceAnalysisOutput {
        efficiencyClass
        type
      }
      ... on EuTaxonomyComplianceAnalysisOutput {
        euTaxonomyCompliance
        type
      }
      ... on ReportSlfComplianceAnalysisOutput {
        reportId
        type
        issuedAt
      }
    }
    notCompliantFactors {
      level
      score
      certificateType
    }
  }
`);

export const GET_FILTERED_BUILDINGS = graphql(`
  query StatusQuoAnalysisGetFilteredBuildings($buildingWhere: building_bool_exp!, $limit: Int!, $offset: Int!) {
    building(
      where: $buildingWhere
      limit: $limit
      offset: $offset
      order_by: [{ address: { street: asc } }, { id: asc }]
    ) {
      ...SearchBuilding
    }
    building_aggregate(where: $buildingWhere) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_FILTERED_BUILDING_LIST = graphql(`
  query StatusQuoAnalysisGetFilteredBuildingsFromView(
    $buildingWhere: BuildingWhere!
    $limit: Int
    $offset: Int
    $orderBy: BuildingOrderByInput
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getBuildingList(
      where: $buildingWhere
      limit: $limit
      offset: $offset
      orderBy: $orderBy
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      buildings {
        ...BuildingDetails
      }
      count
    }
  }
`);

export const GET_BENCHMARK = graphql(`
  query StatusQuoAnalysisGetBenchmark(
    $portfolioId: Int
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
    $classOfUse: class_of_use_enum
    $typeOfUse: type_of_use_enum
  ) {
    getBenchmark(
      portfolioId: $portfolioId
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
      classOfUse: $classOfUse
      typeOfUse: $typeOfUse
    ) {
      ... on BuildingsBenchmarkCrremOutput {
        ...BuildingsBenchmarkCrrem
      }
      ... on BuildingsBenchmarkNationalEnergyLawOutput {
        ...BuildingsBenchmarkNationalEnergyLaw
      }
    }
  }
`);

// Per-graph demand portfolio queries. Each renders independently instead of the
// whole dashboard blocking on a single monolithic query.
// See plans/split-demand-portfolio-analysis-query.md.

export const GET_ANALYZED_BUILDINGS_CORE = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsCore(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsCore(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      analysisType
      analyzedBuildingsCount
      buildingsWithoutEnergyPaths
      economicUnitsCount
      floorAreaByClassOfUse {
        classOfUse
        area
      }
      minConstructionYear
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_SLF_COMPLIANCE = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsSlfCompliance(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsSlfCompliance(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      slfCompliantBuildingsCount
      slfCompliantEconomicUnitsCount
      slfFullAssessmentBuildingsCount
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_GENERAL_DATA = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsGeneralData(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsGeneralData(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      area
      address
      country
      buildingId
      economicUnitId
      co2Intensity
      typeOfUse
      finalEnergy
      year
      mainEnergySourceType
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_CLIMATE_RISKS = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsClimateRisks(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsClimateRisks(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      type
      timespan
      value
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_KPIS = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsKpis(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsKpis(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      averageFinalEnergy
      averagePrimaryEnergy
      averageHeatingDemand
      averageCo2Emissions
      co2CostAtYear
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_COST_PATHS = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsCostPaths(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsCostPaths(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      energyCostPath {
        total
        m2
      }
      co2CostDistributionPath {
        landlordPath {
          total
          m2
        }
        tenantPath {
          total
          m2
        }
      }
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_BY_TAX_BRACKET = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsByTaxBracket(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsByTaxBracket(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      taxBracket
      count
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_BY_EFFICIENCY_CLASS = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsByEfficiencyClass(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsByEfficiencyClass(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      efficiencyClass
      count
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_EU_TAXONOMY = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsEuTaxonomy(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsEuTaxonomy(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      euTaxonomyCompliance
      count
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_BY_ENERGY_SOURCE_TYPE = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsByEnergySourceType(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsByEnergySourceType(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      energySourceType
      count
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_ENERGY_DATA = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsEnergyData(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsEnergyData(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      energySourceType
      energyConsumptionTotal
      energyConsumptionM2
    }
  }
`);

graphql(`
  fragment AnalyzeBuildingsCrremAnalysisResult on AnalyzeBuildingsCrremAnalysisResultOutput {
    analysisType
    crremAnalysis {
      ...AnalyzeBuildingsCrremAnalysis
    }
    customTargets {
      ...CustomTargets
    }
    customTargetPath {
      co2 {
        targetPath
        intersectionYear
      }
    }
  }
`);

graphql(`
  fragment AnalyzeBuildingsNationalEnergyLawAnalysisResult on AnalyzeBuildingsNationalEnergyLawAnalysisResultOutput {
    analysisType
    nationalEnergyLawAnalysis {
      ...AnalyzeBuildingsNationalEnergyLawAnalysis
    }
    customTargets {
      ...CustomTargets
    }
  }
`);

export const GET_ANALYZED_BUILDINGS_ANALYSIS = graphql(`
  query StatusQuoAnalysisGetAnalyzedBuildingsAnalysis(
    $where: BuildingWhere!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuildingsAnalysis(
      where: $where
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      ... on AnalyzeBuildingsCrremAnalysisResultOutput {
        ...AnalyzeBuildingsCrremAnalysisResult
      }
      ... on AnalyzeBuildingsNationalEnergyLawAnalysisResultOutput {
        ...AnalyzeBuildingsNationalEnergyLawAnalysisResult
      }
    }
  }
`);

export const GET_ANALYZED_BUILDING = graphql(`
  query StatusQuoAnalysisBuildingGetBuilding(
    $buildingId: Int!
    $year: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getAnalyzedBuilding(
      building_id: $buildingId
      year: $year
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      ... on AnalyzeBuildingCrremOutput {
        ...AnalyzeBuildingCrrem
      }
      ... on AnalyzeBuildingNationalEnergyLawOutput {
        ...AnalyzeBuildingNationalEnergyLaw
      }
    }
  }
`);

export const QUEUE_STATUS_QUO_ANALYSIS_PDF_EXPORT = graphql(`
  mutation QueueStatusQuoAnalysisPdfExport(
    $buildingId: Int!
    $title: String
    $description: String
    $useCustomTargetPath: Boolean
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    queueEsgAnalysisPdfExport(
      buildingId: $buildingId
      title: $title
      description: $description
      useCustomTargetPath: $useCustomTargetPath
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      asyncJobId
    }
  }
`);

export const QUEUE_DETAILED_ONE_PAGER_PDF_EXPORT = graphql(`
  mutation QueueSummaryOnePagerPdfExport(
    $buildingId: Int!
    $title: String
    $description: String
    $tab: status_quo_pdf_export_tab_enum
    $reportingYear: Int
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
    $displayBase: String
    $benchmarkPortfolioId: Int
    $classOfUse: class_of_use_enum
    $typeOfUse: type_of_use_enum
    $crremPathGraphType: crrem_path_graph_type_enum
    $crremPathsType: String
    $demandScopePerspective: scope_perspective_enum
    $discountRate: Float
    $consumptionScopePerspective: scope_perspective_enum
  ) {
    queueSummaryOnePagerPdfExport(
      buildingId: $buildingId
      title: $title
      description: $description
      tab: $tab
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
      displayBase: $displayBase
      benchmarkPortfolioId: $benchmarkPortfolioId
      classOfUse: $classOfUse
      typeOfUse: $typeOfUse
      crremPathGraphType: $crremPathGraphType
      crremPathsType: $crremPathsType
      demandScopePerspective: $demandScopePerspective
      discountRate: $discountRate
      consumptionScopePerspective: $consumptionScopePerspective
    ) {
      asyncJobId
    }
  }
`);

export const QUEUE_ECONOMIC_UNIT_ESG_ANALYSIS_PDF_EXPORT = graphql(`
  mutation QueueEconomicUnitEsgAnalysisPdfExport(
    $economicUnitId: Int!
    $title: String
    $description: String
    $useCustomTargetPath: Boolean
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    queueEconomicUnitEsgAnalysisPdfExport(
      economicUnitId: $economicUnitId
      title: $title
      description: $description
      useCustomTargetPath: $useCustomTargetPath
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      asyncJobId
    }
  }
`);

/**
 * Batch export of every building's ESG-analysis one-pager in a scope (a single
 * transaction/economic unit, or a single portfolio) as one zip. Exactly one of
 * `economicUnitId` / `portfolioId` must be provided. Returns the tracking
 * `asyncJobId` (fed to `trackAsyncJob`) plus `totalBuildings` for messaging.
 */
export const QUEUE_BATCH_ESG_ANALYSIS_PDF_EXPORT = graphql(`
  mutation QueueBatchEsgAnalysisPdfExport(
    $economicUnitId: Int
    $portfolioId: Int
    $title: String
    $description: String
    $useCustomTargetPath: Boolean
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    queueBatchEsgAnalysisPdfExport(
      economicUnitId: $economicUnitId
      portfolioId: $portfolioId
      title: $title
      description: $description
      useCustomTargetPath: $useCustomTargetPath
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      asyncJobId
      totalBuildings
    }
  }
`);

export const STATUS_QUO_ANALYSIS_CONSUMPTION_ANALYSIS = graphql(`
  query StatusQuoAnalysisConsumptionAnalysis(
    $buildingId: Int!
    $fromYear: Int!
    $toYear: Int!
    $referenceAreaType: analysis_reference_area_enum
  ) {
    getConsumptionAnalysis(
      building_id: $buildingId
      fromYear: $fromYear
      toYear: $toYear
      referenceAreaType: $referenceAreaType
    ) {
      data {
        averageAbsolute
        average
        series {
          count
          from
          to
          sumAbsolute
          sum
        }
        type
      }
    }
  }
`);

export const STATUS_QUO_ANALYSIS_CONSUMPTION_ALLOCATION = graphql(`
  query StatusQuoAnalysisConsumptionAllocation(
    $buildingId: Int!
    $year: Int!
    $perspective: scope_perspective_enum
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionAllocation(
      building_id: $buildingId
      year: $year
      perspective: $perspective
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      carbonEmissions {
        ...ConsumptionAllocationCrremCarbonEmission
        ...ConsumptionAllocationNationalEnergyLawCarbonEmission
      }
      co2Tax {
        costShare
        total
        totalAbsolute
        forecastItems {
          landlordShare
          landlordShareAbsolute
          landlordSharePercentage
          tenantShare
          tenantShareAbsolute
          tenantSharePercentage
          year
        }
      }
      energyConsumptions {
        items {
          percentage
          type
          value
          valueAbsolute
        }
        total
        totalAbsolute
        type
      }
    }
  }
`);

export const GET_CONSUMPTION_ALLOCATION_BY_SCOPE123_PERSPECTIVE = graphql(`
  query GetConsumptionAllocationByScope123Perspective(
    $buildingId: Int!
    $year: Int!
    $perspective: scope_perspective_enum
  ) {
    getConsumptionAllocationByScope123Perspective(building_id: $buildingId, year: $year, perspective: $perspective) {
      carbonEmissions {
        ...ConsumptionAllocationCrremCarbonEmission
        ...ConsumptionAllocationNationalEnergyLawCarbonEmission
      }
    }
  }
`);

export const GET_BUILDING_CRREM_CONSUMPTION_ANALYSIS = graphql(`
  query getBuildingCRREMConsumptionAnalysis(
    $buildingId: Int!
    $year: Int!
    $referenceAreaType: analysis_reference_area_enum
  ) {
    getBuildingCRREMConsumptionAnalysis(building_id: $buildingId, year: $year, referenceAreaType: $referenceAreaType) {
      ...BuildingCRREMConsumptionAnalysisOutput
    }
  }
`);

export const GET_CONSUMPTION_COVERAGE = graphql(`
  query GetConsumptionCoverage(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionCoverage(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      buildingsWithoutConsumptionData
      totalBuildings
    }
  }
`);

export const GET_CONSUMPTION_FINAL_ENERGY_DEVELOPMENT = graphql(`
  query GetConsumptionFinalEnergyDevelopment(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionFinalEnergyDevelopment(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      year
      heatingBreakdown {
        percentage
        valueRelative
        valueAbsolute
      }
      electricityBreakdown {
        percentage
        valueRelative
        valueAbsolute
      }
      totalAbsolute
      totalRelative
    }
  }
`);

export const GET_CONSUMPTION_DEVIATION_FROM_DEMAND = graphql(`
  query GetConsumptionDeviationFromDemand(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionDeviationFromDemand(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      lt_neg_30
      neg_30_neg_10
      neg_10_10
      pos_10_30
      gt_30
    }
  }
`);

export const GET_CONSUMPTION_ENERGY_SOURCES_BY_FINAL_ENERGY = graphql(`
  query GetConsumptionEnergySourcesByFinalEnergy(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionEnergySourcesByFinalEnergy(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      energySourceType
      percentage
      valueAbsolute
      valueRelative
    }
  }
`);

export const GET_CONSUMPTION_ENERGY_SOURCES_BY_CO2_INTENSITY = graphql(`
  query GetConsumptionEnergySourcesByCO2Intensity(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionEnergySourcesByCO2Intensity(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      energySourceType
      percentage
      valueAbsolute
      valueRelative
    }
  }
`);

export const GET_CONSUMPTION_ENERGY_SOURCES_BY_BUILDING_COUNT = graphql(`
  query GetConsumptionEnergySourcesByBuildingCount(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionEnergySourcesByBuildingCount(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      energySourceType
      count
    }
  }
`);

export const GET_CONSUMPTION_CO2_INTENSITY_DEVELOPMENT = graphql(`
  query GetConsumptionCO2IntensityDevelopment(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionCO2IntensityDevelopment(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      year
      valueRelative
      valueAbsolute
    }
  }
`);

export const GET_CONSUMPTION_CRREM_PATHS = graphql(`
  query GetConsumptionCRREMPaths(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionCRREMPaths(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      co2PathRelative
      co2TargetPathRelative
      energyPathRelative
      energyTargetPathRelative
      strandingData {
        year
        count
      }
      excessEmissionCostPath
      strandingYear
      yearOfExcedance
    }
  }
`);

export const GET_CONSUMPTION_PORTFOLIO_KPIS = graphql(`
  query GetConsumptionPortfolioKPIs(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getConsumptionPortfolioKPIs(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      finalEnergyRelative
      finalEnergyTotal
      primaryEnergyRelative
      primaryEnergyTotal
      co2Relative
      co2Total
      energyCostRelative
      energyCostTotal
      co2TaxRelative
      co2TaxTotal
    }
  }
`);

export const GET_BUILDING_CONSUMPTION_KPIs = graphql(`
  query getStatusQuoConsumptionAnalysisKPIs(
    $buildingId: Int!
    $year: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
  ) {
    getEsgConsumptionAnalysisKPIs(
      buildingId: $buildingId
      year: $year
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
    ) {
      co2Intensity
      co2IntensityAbsolute
      co2Tax
      co2TaxAbsolute
      efficiencyClass
      energyCost
      energyCostAbsolute
      euTaxonomy
      finalEnergy
      finalEnergyAbsolute
      primaryEnergy
      primaryEnergyAbsolute
    }
  }
`);

graphql(`
  fragment PortfolioBasicDetails on portfolio {
    id
    name
  }
`);

graphql(`
  fragment EconomicUnitBasicDetails on economic_unit {
    id
    name
    description
    custom_data
    customer_economic_unit_id
    portfolio {
      id
    }
    buildings {
      address {
        country_id
      }
    }
  }
`);

export const GET_PORTFOLIO_COUNT = graphql(`
  query PortfolioCount {
    portfolio_aggregate {
      aggregate {
        count
      }
    }
  }
`);

export const GET_PORTFOLIOS_AGGREGATE_COUNT = graphql(`
  query getPortfoliosAggregateCount($where: portfolio_bool_exp!) {
    portfolio_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_ECONOMIC_UNIT_COUNT = graphql(`
  query EconomicUnitCount {
    economic_unit_aggregate(where: { buildings: { state: { _eq: ACTIVE } } }) {
      aggregate {
        count
      }
    }
  }
`);

export const GET_PORTFOLIOS_BY_IDS = graphql(`
  query getPortfolioByIds($where: portfolio_bool_exp!) {
    portfolio(where: $where) {
      ...PortfolioBasicDetails
    }
  }
`);

export const GET_PORTFOLIOS_BY_SEARCHED_STRING = graphql(`
  query getPortfoliosBySearchString($limit: Int!, $offset: Int!, $where: portfolio_bool_exp!) {
    portfolio(order_by: { name: asc }, limit: $limit, offset: $offset, where: $where) {
      ...PortfolioBasicDetails
    }
  }
`);

export const GET_BUILDINGS_BY_SEARCHED_STRING = graphql(`
  query SearchBuildings($limit: Int!, $offset: Int!, $text: String!) {
    building(
      where: {
        _or: [
          { address: { street: { _ilike: $text } } }
          { address: { city: { _ilike: $text } } }
          { address: { postal_code: { _ilike: $text } } }
          { customer_external_identifier: { _ilike: $text } }
        ]
      }
      limit: $limit
      offset: $offset
      order_by: { address: { street: asc } }
    ) {
      ...SearchBuilding
    }
  }
`);

export const GET_SINGLE_BUILDING_DATA = graphql(`
  query getSingleBuildingBuilding($buildingId: Int!) {
    building_by_pk(id: $buildingId) {
      ...StatusQuoAnalysisBuildingBasicDetails
    }
  }
`);

export const GET_ECONOMIC_UNITS_BY_SEARCHED_STRING = graphql(`
  query getEconomicUnitsBySearchString($limit: Int!, $offset: Int!, $text: String!) {
    economic_unit(
      order_by: { name: asc }
      limit: $limit
      offset: $offset
      where: { _and: [{ name: { _ilike: $text } }, { buildings: { state: { _eq: ACTIVE } } }] }
    ) {
      ...EconomicUnitBasicDetails
    }
  }
`);

export const GET_ECONOMIC_UNITS_BY_IDS = graphql(`
  query getEconomicUnitsByIds($ids: [Int!]!) {
    economic_unit(where: { _and: [{ id: { _in: $ids } }, { buildings: { state: { _eq: ACTIVE } } }] }) {
      ...EconomicUnitBasicDetails
    }
  }
`);

export const GET_BUILDINGS_BY_IDS = graphql(`
  query getBuildingsByIds($ids: [Int!]!) {
    building(where: { id: { _in: $ids } }) {
      ...SearchBuilding
    }
  }
`);

export const SEARCH_PORTFOLIOS = graphql(`
  query SearchPortfolios($limit: Int!, $offset: Int!, $text: String!) {
    portfolio(where: { name: { _ilike: $text } }, limit: $limit, offset: $offset, order_by: { name: asc }) {
      id
      name
      folder {
        id
      }
    }
  }
`);

export const SEARCH_ECONOMIC_UNITS = graphql(`
  query SearchEconomicUnits($limit: Int!, $offset: Int!, $text: String!) {
    economic_unit(where: { name: { _ilike: $text } }, limit: $limit, offset: $offset, order_by: { name: asc }) {
      id
      name
      folder {
        id
      }
    }
  }
`);

export const CREATE_SHARED_BUILDING_SNAPSHOT = graphql(`
  mutation CreateSharedBuildingSnapshot($buildingId: Int!) {
    createSharedBuildingSnapshot(buildingId: $buildingId) {
      hash
    }
  }
`);

export const GET_SHARED_BUILDING_SNAPSHOT = graphql(`
  query GetSharedBuildingSnapshot($hash: String!) {
    getSharedBuildingSnapshot(hash: $hash) {
      data
      created_at
    }
  }
`);

export const GET_CONSUMPTION_BUILDING_LIST = graphql(`
  query GetConsumptionBuildingList(
    $where: BuildingWhere!
    $reportingYear: Int!
    $referenceAreaType: analysis_reference_area_enum
    $emissionFactorSource: emission_factor_source_enum
    $orderBy: ConsumptionBuildingOrderByInput
    $limit: Int!
    $offset: Int!
  ) {
    getConsumptionBuildingList(
      where: $where
      reportingYear: $reportingYear
      referenceAreaType: $referenceAreaType
      emissionFactorSource: $emissionFactorSource
      orderBy: $orderBy
      limit: $limit
      offset: $offset
    ) {
      buildings {
        id
        street
        postalCode
        city
        country
        typesOfUse
        isNewConstruction
        co2Relative
        co2Total
        co2TaxRelative
        co2TaxTotal
        primaryEnergyRelative
        primaryEnergyTotal
        finalEnergyRelative
        finalEnergyTotal
        deviationFromDemand
      }
      count
    }
  }
`);
